import { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, CheckCircle, ExternalLink, Clock } from 'lucide-react';
import { useNotifications } from '../../../features/community/hooks';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Drawer } from 'antd';

/**
 * Renders a single notification row — used in both the dropdown and drawer.
 */
function NotificationRow({ notification: n, onClick, size = 'sm' }) {
  const avatarSize = size === 'lg' ? 'w-12 h-12' : 'w-10 h-10';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left ${size === 'lg' ? 'p-5' : 'px-5 py-4'} flex gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors relative group ${!n.is_read ? 'bg-primary/5' : ''}`}
    >
      {!n.is_read && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
      )}

      <div className="flex-shrink-0 mt-0.5">
        {n.actor?.avatar_url ? (
          <img
            src={n.actor.avatar_url}
            alt=""
            className={`${avatarSize} rounded-full object-cover shadow-sm`}
          />
        ) : (
          <div className={`${avatarSize} rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 font-bold text-sm`}>
            {n.actor?.username?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug mb-1">
          <span className="font-bold text-gray-900 dark:text-white">
            {n.actor?.name || n.actor?.username}
          </span>{' '}
          {n.verb || n.message?.split('"')[0]?.replace(n.actor?.name || '', '')?.replace(n.actor?.username || '', '')?.trim()}
          {n.post_title && (
            <span className="font-semibold text-gray-900 dark:text-white"> &ldquo;{n.post_title}&rdquo;</span>
          )}
        </p>

        <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
          </span>
          {size === 'sm' && (n.post_slug || n.notification_type === 'follow') && (
            <span className="flex items-center gap-1 group-hover:text-primary transition-colors">
              <ExternalLink className="w-3 h-3" />
              {n.notification_type === 'follow' ? 'View Profile' : 'View'}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const dropdownRef = useRef(null);
  const observerTarget = useRef(null);
  const isFetchingRef = useRef(false);
  const navigate = useNavigate();
  const { user } = useCommunityAuth();
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1280 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1280);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications({ enabled: !!user });

  // Fetch when dropdown opens
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Infinite scroll observer for drawer
  useEffect(() => {
    if (!isDrawerOpen || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingRef.current) {
          isFetchingRef.current = true;
          const nextPage = page + 1;
          fetchNotifications({ page: nextPage })
            .then((data) => {
              setPage(nextPage);
              if (!data?.next) setHasMore(false);
            })
            .catch(() => {
              // Don't increment page on error — user can scroll again to retry
            })
            .finally(() => {
              isFetchingRef.current = false;
            });
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [isDrawerOpen, hasMore, loading, page, fetchNotifications]);

  const openDrawer = useCallback(() => {
    setPage(1);
    setHasMore(true);
    setIsDrawerOpen(true);
    fetchNotifications();
  }, [fetchNotifications]);

  const handleBellClick = () => {
    if (isMobile) {
      openDrawer();
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleNotificationClick = async (n) => {
    if (!n.is_read) {
      await markAsRead(n.id);
    }
    setIsOpen(false);
    setIsDrawerOpen(false);

    if (n.notification_type === 'follow' && n.actor?.username) {
      navigate(`/profile/${n.actor.username}`);
    } else if (n.post_slug) {
      const basePath = n.post_type === 'forum' ? '/forum' : '/blog';
      navigate(`${basePath}/${n.post_slug}`);
    }
  };

  // Keep large counts compact like modern messaging apps.
  const badgeCount = (() => {
    if (unreadCount <= 99) return unreadCount;
    if (unreadCount < 1000) return '99+';
    const thousands = Math.floor(unreadCount / 1000);
    return `${thousands}k+`;
  })();

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon */}
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
        aria-label="Notifications"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex min-w-[1rem] h-4 px-1.5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white dark:ring-gray-900">
            {badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel (desktop only) */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 max-h-[32rem] overflow-hidden rounded-2xl bg-white dark:bg-[#161616] shadow-2xl ring-1 ring-black/5 dark:ring-white/10 z-[100] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-base font-bold text-gray-900 dark:text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-1.5"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Mark all as read
              </button>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1 notification-scrollbar">
            {loading && notifications.length === 0 ? (
              <div className="py-12 text-center">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-primary border-r-transparent mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading your alerts...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-16 px-6 text-center">
                <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                </div>
                <p className="text-gray-900 dark:text-white font-medium mb-1">No notifications yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">We&apos;ll notify you when someone interacts with your posts.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onClick={() => handleNotificationClick(n)}
                    size="sm"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-black/20">
            <button
              onClick={() => {
                setIsOpen(false);
                openDrawer();
              }}
              className="block w-full py-2 text-center text-xs font-bold text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}

      {/* Full Notifications Drawer */}
      <Drawer
        title={
          <div className="flex items-center justify-between pr-8">
            <span className="text-lg font-bold text-gray-900 dark:text-white">All Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs font-medium text-primary hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>
        }
        placement="right"
        width={isMobile ? '100%' : 450}
        onClose={() => {
          setIsDrawerOpen(false);
          setPage(1);
          setHasMore(true);
        }}
        open={isDrawerOpen}
        className="[&_.ant-drawer-header]:border-b [&_.ant-drawer-header]:border-gray-100 [&_.ant-drawer-header]:dark:border-gray-800 [&_.ant-drawer-content]:dark:bg-gray-900 [&_.ant-drawer-header]:dark:bg-gray-900 [&_.ant-drawer-title]:dark:text-white [&_.ant-drawer-close]:dark:text-gray-400"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto notification-scrollbar">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500 dark:text-gray-400">
                <Bell className="w-12 h-12 mb-4 opacity-20" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {notifications.map((n) => (
                  <NotificationRow
                    key={`drawer-${n.id}`}
                    notification={n}
                    onClick={() => handleNotificationClick(n)}
                    size="lg"
                  />
                ))}

                {/* Sentinel element for intersection observer */}
                {hasMore && <div ref={observerTarget} className="h-4 w-full" />}

                {loading && (
                  <div className="p-6 flex justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-r-transparent" />
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </Drawer>
    </div>
  );
}
